import React, { createContext, ReactNode, useEffect, useState } from "react";
import { useToast } from "hooks";
import { GameLiftStreams, setLogLevel } from "thirdparty/gameliftstreamssdk/gameliftstreams-1.0.0";
import { RegisterTouchToMouse } from "thirdparty/gameliftstreamssdk/touchtomouse.js";
import { createStream, getSignalResponse } from "api/webservice/stream";

interface GameLiftStreamsProviderProps {
  sessionId: string,
  children: ReactNode
}

const GAMELIFTSTREAMS_CONNECTION_DELAY = 300;

export interface IGameLiftStreamsContext {
  streamVideoRef: any;
  streamAudioRef: any;
  gameConnected: boolean;
  gameStarted: boolean;
  gameLoading: boolean;
  gameFocused: boolean;
  gameLiftStreamsInput: boolean;
  gameState: any;
  errorOccurred: boolean;
  retried: boolean;
  toggleGameFocus: () => void;
  toggleGameConnected: () => void;
  toggleGameStarted: () => void;
  toggleGameLiftStreamsInput: (b: boolean) => void;
  initGameLiftStreams: () => void;
  gameDisconnect: (b?: boolean) => void;
}

setLogLevel("debug");

export const GameLiftStreamsContext = createContext<IGameLiftStreamsContext>(null!);

export const GameLiftStreamsProvider: React.FC<GameLiftStreamsProviderProps> = ({
  children,
  sessionId
}: GameLiftStreamsProviderProps) => {
     const toast = useToast();
  const streamVideoRef = React.useRef<HTMLVideoElement>(null!);
  const streamAudioRef = React.useRef<HTMLAudioElement>(null!);
  const [gameLiftStreamsConnection, setGameLiftStreamsConnection] = useState(false);
  const [gameLiftStreams, setGameLiftStreams] = useState<GameLiftStreams | null>(null);
  const [gameLiftStreamsInput, toggleGameLiftStreamsInput] = useState(false);
  const [gameLoading, updateLoadStatus] = useState(false);
  const [gameConnected, setGameConnected] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameFocused, setGameFocused] = useState(false);
  const [gameState, setGameState] = useState("connecting");
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorOccurred, setErrorOccurred] = useState(false);
  //may not need this variable, was experimenting with getting the game to appear after automatically retrying for a new stream
  const [retried, setRetried] = useState(false);

  useEffect(() => {
    switch (gameState) {
      case "connecting":
        if (!isConnecting) setIsConnecting(true);
        break;
      case "connected":
        // we need to add a delay to make sure that gameliftstreams is
        // fully initialized after a focus switch
        if (!isConnecting) setIsConnecting(true);
        new Promise((resolve) => {
          setTimeout(resolve, GAMELIFTSTREAMS_CONNECTION_DELAY)
        })
          .then(() => setIsConnecting(false));
        break;
      case "failed":
        //maybe add a retry function call here?
        /* setIsConnecting(false); */

        //reset to initial states for retry
        /* updateLoadStatus(false);
        sessionStorage.setItem('gameStarted', 'false');
        setGameConnected(false);
        setGameStarted(false);
        setGameFocused(false);
        setGameLiftStreamsConnection(false);
        setGameState("connecting");
        generateStreamSignalRequest(); */
        setIsConnecting(true);
        setGameConnected(false);
        break;
      case "disconnected":
        setIsConnecting(true);
        setGameConnected(false);
        break
      case "closed":
        setIsConnecting(true);
        setGameConnected(false);
        break
      default:
        setIsConnecting(true);
    }
  }, [gameState])


  // TODO refactor this
  useEffect(() => {
    generateStreamSignalRequest();
  }, [gameLiftStreams, gameConnected]);
   	
  useEffect(() => {
    if (!gameLiftStreamsConnection) {
      console.log("wait for gameliftstreams stream to connect");
    } else {
      setInput(gameLiftStreamsInput);
    }
  }, [gameLiftStreamsInput, gameLiftStreamsConnection]);
   	
  const generateStreamSignalRequest = () => {
    if (gameLiftStreams && gameConnected && !gameLiftStreamsConnection) {
      //console.log("gameLiftStreams exists and gameStarted is true");
      const signalRequest = gameLiftStreams.generateSignalRequest();
      console.log("signalRequest: ", signalRequest);
      const getSignalResponseDelayMilliSec = 1000;
      const iterate = (timeWaiting: number) => {
        //console.log('Waiting...er');
        const p = new Promise((resolve) => {
          setTimeout(resolve, getSignalResponseDelayMilliSec);
        });
        p.then(() => getSignalResponse(sessionId)
          .then(res => {
            if (res.SignalResponse?.length > 0) {
              console.log("signalresponse returned from iterate");
              gameLiftStreams.processSignalResponse(res.SignalResponse)
                .then(() => {
                  setGameLiftStreamsConnection(true);
                  toggleGameLiftStreamsInput(true);
                  setInput(true);
                });
              updateLoadStatus(false);
            } else if (timeWaiting < 120000) {
              iterate(timeWaiting + getSignalResponseDelayMilliSec);
            } else {
              console.log('timed out');
              updateLoadStatus(false);
              sessionStorage.setItem('gameStarted', 'false');
              setGameStarted(false);
              setGameFocused(false);
              setErrorOccurred(true);
              toast({
                title: "Timed out",
                description: "Connection timed out, please try again.",
                status: "error"
              });
            };
          }).catch(err => {
            console.log("Error when trying to iterate", err);
            setErrorOccurred(true);
            toast({
              title: "Error",
              description: "An issue occurred when establishing the stream connection, please try again.",
              status: "error"
            });
          }))
          .catch(err => {
            console.log('error 404', err);
            updateLoadStatus(false);
            sessionStorage.setItem('gameStarted', 'false');
            setGameStarted(false);
            setGameFocused(false);
            setErrorOccurred(true);
            toast({
              title: "Error",
              description: "Couldn't connect to stream, try again.",
              status: "error"
            });
          });
      }

      createStream(signalRequest, sessionId).then((res) => {
        if (res.SignalResponse?.length > 0) {
          console.log("signalresponse returned from from init");
          gameLiftStreams.processSignalResponse(res.SignalResponse)
            .then(() => {
              setGameLiftStreamsConnection(true);
              toggleGameLiftStreamsInput(true);
              setInput(true);
            }).catch(err => {
              console.log("Error 401");
              setErrorOccurred(true);
            });
          updateLoadStatus(false);
        }
        else {
          iterate(0);
        }
      }).catch(err => {
        console.log("Error 401 in createStream");
        //iterate(0);
        setGameStarted(false);
        setGameStarted(true);
        setErrorOccurred(true);
      });
    }
  }

  function initializeGameLiftStreams(retries=0) {
    if (streamVideoRef.current) {
      setGameLiftStreams(new GameLiftStreams({
      videoElement: streamVideoRef.current,
      audioElement: streamAudioRef.current,
      inputConfiguration: {
        autoMouse: true,
        autoKeyboard: true,
        autoGamepad: true,
        hapticFeedback: true,
        setCursor: 'visibility',
        autoPointerLock: 'fullscreen',
      },
      clientConnection: {
        connectionState: streamConnectionStateCallback,
        channelError: streamChannelErrorCallback,
        serverDisconnect: streamServerDisconnectCallback,
        applicationMessage: streamApplicationMessageCallback,
      }
    }));

    // For touchscreen support, convert inputs to mouse events (optional)
    RegisterTouchToMouse(streamVideoRef.current);
    }
    else if(retries < 10) {
      console.log("retries: ", retries);
      retries++;
      setTimeout(() => initializeGameLiftStreams(retries), 5000);
    } else if (retries >= 10) {
      console.log("retries limit reached");
    }
  }

  function setInput(input: boolean) {
    if (gameLiftStreams && input) {
      //console.log("input attached");
      gameLiftStreams.attachInput();
    } else if (gameLiftStreams && input == false) {
      //console.log("input detached");
      gameLiftStreams.detachInput();
    } else {
      console.log("setInput gameLiftStreams might be undefined: ", gameLiftStreams);
    }
  }

  const appDisconnect = (gameEnding=true) => {
    /*     toast({
          title: "Disconnected",
          description:"Charisma has been disconnected- please refresh to reconnect.",
          status: "info"
        }); */
    try{
      setInput(false);
      gameLiftStreams?.close();
    }
    catch (e) {
      console.warn(e);
      setErrorOccurred(true);
    }
    if (gameEnding) {
      sessionStorage.setItem('gameStarted', 'false');
      setGameConnected(false);
      setGameStarted(false);
      setGameFocused(false);
    }
  }

    const toastOnDisconnect = () => {
      setErrorOccurred(true);
      toast({
      title: "Error",
      description: "Couldn't connect to stream, please try again.",
      status: "error"
    });
  }

  const toastOnFailed = () => {
    setErrorOccurred(true);
    toast({
      title: "Error",
      description: "Stream connection failed, please try again.",
      status: "error"
    });
  }

  const toastOnRetry = () => {
    toast({
      id: "retryToast",
      title: "Retrying Charisma",
      description: "Trying again to reconnect...",
      status: "info",
      duration: null
    });
  };

  function closeToast(toastId: string) {
    toast.close(toastId);
  };

  function streamConnectionStateCallback(state: string) {
    setGameState(state);
    if (state === 'connected') {
      closeToast("retryToast");
    }
    if (state === 'disconnected') {
      appDisconnect(true);
      toastOnDisconnect();
    }
    if (state === 'failed') {
      //maybe add a retry call here?
      /* appDisconnect(true); */
      toastOnFailed();
      retryStreamConnection();
    }
  }

  function streamChannelErrorCallback(error: any) {
    console.log('WebRTC internal connection error: ' + error);
    appDisconnect(true);
    toastOnFailed();
  }

  function streamServerDisconnectCallback(reasoncode: string) {
    console.log('Server disconnected with reason: ' + reasoncode);
    if (reasoncode === 'terminated') {
      //deleteAllQueryParams();
      setErrorOccurred(true);
    }
    // The connection state will transition to 'disconnected' within 5 seconds,
    // but there is no reason to wait. The client can disconnect immediately.
    appDisconnect(true);
    toastOnDisconnect();
  }

  function streamApplicationMessageCallback(message: any) {
    console.log('Application disconnected with message: ' + message);
    appDisconnect(true);
    toastOnDisconnect();
  }

  function retryStreamConnection() {
    toastOnRetry();
    setRetried(true);
    //reset to initial states for retry
    setIsConnecting(false);
    updateLoadStatus(false);
    sessionStorage.setItem('gameStarted', 'false');
    setGameConnected(false);
    setGameStarted(false);
    setGameFocused(false);
    setGameLiftStreamsConnection(false);
    setGameState("connecting");
    generateStreamSignalRequest();
    //reset error state as well
    setErrorOccurred(false);
   }

  async function reloadVideo(attempts= 0) {
    if (attempts < 2) {
      setTimeout( () => {
        if (streamVideoRef.current) {
          console.log("reloading video", streamVideoRef.current);
          streamVideoRef.current.load();
        }
        else {
          console.log("retrying to reload video")
          reloadVideo(attempts++);
        }
      }, 500)
    }
  }

  function toggleGameFocus() {
    setGameFocused(!gameFocused);
    toggleGameLiftStreamsInput(!gameFocused);
    try {
      setInput(!gameFocused);
    }
    catch (e){
      console.warn(e)
    }
  }

  function toggleGameConnected() {
    setGameConnected(!gameConnected);
  }
   	
  function toggleGameStarted() { 	
    setGameStarted(!gameStarted);
  }

  function initGameLiftStreams() {
    sessionStorage.setItem('session.id', sessionId);
    /* sessionStorage.setItem('gameStarted', 'true');
    setGameStarted(true); */
    /* setGameFocused(true); */
    initializeGameLiftStreams();
  }

  return (
    <GameLiftStreamsContext.Provider
    value={{
      streamVideoRef,
      streamAudioRef,
      gameConnected,
      gameStarted,
      gameLoading,
      gameFocused,
      gameLiftStreamsInput,
      gameState,
      errorOccurred,
      retried,
      toggleGameStarted,
      toggleGameConnected,
      toggleGameFocus,
      toggleGameLiftStreamsInput,
      initGameLiftStreams,
      gameDisconnect: appDisconnect
    }}
    >
    {children}
    </GameLiftStreamsContext.Provider>
  );

}
